import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckCircle,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiArrowRight,
  FiTarget,
  FiZap,
  FiDollarSign,
  FiSend,
  FiCheck,
  FiAlertCircle,
  FiSearch,
  FiBarChart,
  FiImage,
  FiLayout,
  FiMail
} from 'react-icons/fi';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Logo } from '../components/common/Logo';

export interface CountryConfig {
  country: string;
  currencySymbol: string;
  prices: { basic: string; prime: string; elite: string; supreme: string };
  costComparison: { role: string; cost: string }[];
  timezoneQuestion: string;
  timezoneAnswer: string;
  agencyLabel: string;
  thankYouPath: string;
  phonePlaceholder: string;
  cityPlaceholder: string;
  testimonials: { quote: string; author: string; result: string }[];
  docTitle: string;
  docDescription: string;
}

interface Props {
  config: CountryConfig;
}

export const WhiteLabelLandingTemplate: React.FC<Props> = ({ config }) => {
  const navigate = useNavigate();

  useDocumentTitle(config.docTitle, config.docDescription);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    websiteUrl: '',
    cityState: '',
    employeeSize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [connectingMessage, setConnectingMessage] = useState('');
  const pricingSectionImageSources = [
    '/images/us-landing/pain-point-image2.png',
    '/images/us-landing/pain-point-image2.jpg.png'
  ];
  const [pricingSectionImageIndex, setPricingSectionImageIndex] = useState(0);
  const [pricingSectionImageMissing, setPricingSectionImageMissing] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const postContact = async (apiUrl: string, payload: object): Promise<Response> => {
    return fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    setConnectingMessage('');

    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://mirani-marketing-website.onrender.com/api';
    const apiUrl = `${apiBaseUrl}/contact`;
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      company: config.agencyLabel,
      message: `White Label Agency Partnership (${config.country}) - Website: ${formData.websiteUrl}, Employee Size: ${formData.employeeSize}, Location: ${formData.cityState}`
    };

    const attemptSubmit = async (): Promise<Response> => {
      try {
        return await postContact(apiUrl, payload);
      } catch {
        setConnectingMessage('Connecting to server, please wait…');
        try {
          await fetch(`${apiBaseUrl}/health`, { method: 'GET' });
        } catch {}
        await new Promise(resolve => setTimeout(resolve, 4000));
        setConnectingMessage('');
        return postContact(apiUrl, payload);
      }
    };

    try {
      const response = await attemptSubmit();
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json() : null;
      if (response.ok) {
        navigate(config.thankYouPath);
      } else {
        setSubmitStatus('error');
        setErrorMessage(data?.message || 'Failed to send message. Please try again.');
      }
    } catch {
      setSubmitStatus('error');
      setErrorMessage('Unable to reach our server. Please email us directly at contact@miranimarketing.com');
    } finally {
      setIsSubmitting(false);
      setConnectingMessage('');
    }
  };

  const services = [
    {
      icon: FiSearch,
      title: 'White Label SEO & AI-SEO',
      features: [
        'Technical SEO optimization',
        'On-page SEO improvements',
        'AI-driven SEO strategies',
        'Keyword research',
        'Content optimization',
        'Competitor analysis',
        'Creating blogs & articles',
        'GA4 & GTM tracking setup',
        'Off page SEO',
        'Technical SEO',
        'Weekly & Monthly KPI reports'
      ]
    },
    {
      icon: FiBarChart,
      title: 'Google Ads Management',
      features: [
        'Campaign creation',
        'Performance optimization',
        'Conversion tracking',
        'A/B testing',
        'Account setup',
        'Conversion tracking setup',
        'Competitor analysis',
        'Creating ad copies',
        'Keyword research',
        'Weekly & Monthly KPI reports'
      ]
    },
    {
      icon: FiUsers,
      title: 'Meta Ads Management',
      features: [
        'Facebook & Instagram campaigns',
        'Audience targeting',
        'Creative testing',
        'Campaign optimization',
        'Account setup',
        'Pixel setup',
        'Competitor analysis',
        'Creating ad copies',
        'Weekly & Monthly KPI reports',
        'Creating banners & editing reels as per project need'
      ]
    },
    {
      icon: FiImage,
      title: 'Creative Production',
      features: ['Ad creatives', 'Social media graphics', 'Marketing assets']
    },
    {
      icon: FiLayout,
      title: 'Landing Pages',
      features: ['Landing page updates', 'CRO improvements', 'Campaign tracking setup']
    }
  ];

  const pricingPlans = [
    {
      name: 'Basic Plan',
      subtitle: 'Best for Freelancers & Small Agencies',
      price: config.prices.basic,
      capacity: 'Up to 35 Active Client Projects',
      executionHours: '200+ execution hours weekly',
      team: ['1 Meta Ads Specialist', '1 Google Ads Specialist', '1 SEO & AI-SEO Specialist', '1 Creative specialist'],
      services: [
        'White-label execution under your agency brand',
        'Full SEO management including technical SEO and AI-SEO',
        'Google Ads and Meta Ads campaign support',
        'Conversion tracking setup',
        'Creating banners for Meta Ads',
        'Creating one landing page if required in project',
        'Handling minor changes in website',
        'All other services included as mentioned above'
      ],
      reporting: ['Weekly progress updates', 'Monthly KPI reports', 'Communication via Email, Slack or Teams'],
      highlighted: false
    },
    {
      name: 'Prime Plan',
      subtitle: 'Best for Growing Agencies',
      price: config.prices.prime,
      capacity: 'Up to 60 Active Client Projects',
      executionHours: '300+ weekly execution hours',
      team: ['1 Meta Ads Specialist', '1 Google Ads Specialist', '1 SEO & AI-SEO Specialist', '1 Creative Specialist', '1 Team Manager'],
      services: [
        'Full white label digital marketing execution',
        'SEO + AI-SEO optimization',
        'Meta Ads & Google Ads campaign management',
        'Creating banners for Meta Ads',
        'Creating one landing page if required in project',
        'Handling minor changes in website'
      ],
      reporting: ['Weekly updates', 'Monthly KPI reports', 'Priority support'],
      highlighted: true
    },
    {
      name: 'Elite Plan',
      subtitle: 'Best for Scaling Agencies',
      price: config.prices.elite,
      capacity: 'Up to 85 Active Client Projects',
      executionHours: '400+ weekly execution hours',
      team: ['1 Meta Ads Specialist', '1 Google Ads Specialist', '1 SEO & AI-SEO Specialist', '1 Creative Specialist', '1 Website Developer', '2 Team Managers'],
      services: [
        'Full white label digital marketing execution',
        'SEO + AI-SEO optimization',
        'Meta Ads & Google Ads campaign management',
        'Creating banners for Meta Ads',
        'Creating one landing page if required in project',
        'Handling minor changes in website'
      ],
      reporting: ['Weekly updates', 'Monthly KPI reports', 'Priority support'],
      highlighted: false
    },
    {
      name: 'Supreme Plan',
      subtitle: 'Best for Large Agencies',
      price: config.prices.supreme,
      capacity: 'Up to 110 Client Projects',
      executionHours: '500+ weekly execution hours',
      team: ['1 Meta Ads Specialist', '1 Google Ads Specialist', '1 SEO & AI-SEO Specialist', '1 Creative Specialist', '1 Website Developer', '2 Team Managers', '1 member will be as per need of projects'],
      services: [
        'Full white label digital marketing execution',
        'SEO + AI-SEO optimization',
        'Meta Ads & Google Ads campaign management',
        'Creating banners for Meta Ads',
        'Creating one landing page if required in project',
        'Handling minor changes in website'
      ],
      reporting: ['Weekly updates', 'Monthly KPI reports', 'Dedicated account manager'],
      highlighted: false
    }
  ];

  const stats = [
    { number: '500+', label: 'Marketing Campaigns Supported' },
    { number: '100+', label: 'Agency Clients Served' },
    { number: '7+', label: 'Years of Digital Marketing Expertise' }
  ];

  const painPoints = [
    'Hiring experienced specialists is extremely expensive',
    'Managing freelancers leads to inconsistent quality',
    'Campaign execution delays damage client relationships',
    'Internal teams become overwhelmed as the agency grows',
    'Payroll costs increase faster than revenue'
  ];

  const whyChoose = [
    {
      icon: FiDollarSign,
      title: 'Reduce Hiring Costs',
      description: `Get a complete team at a fraction of the cost of hiring ${config.country}-based specialists.`
    },
    {
      icon: FiTrendingUp,
      title: 'Scale Without Hiring',
      description: 'Instantly increase delivery capacity without recruitment, onboarding, and training.'
    },
    {
      icon: FiUsers,
      title: 'Dedicated Specialists',
      description: 'Access to SEO experts, paid media specialists, creative designers, and web developers.'
    },
    {
      icon: FiShield,
      title: 'Work Under Your Brand',
      description: 'All services are delivered 100% white label. Your clients only see your agency brand.'
    },
    {
      icon: FiTarget,
      title: 'Focus on Growth',
      description: 'Focus on acquiring clients, growing revenue, and building partnerships instead of managing operations.'
    },
    {
      icon: FiZap,
      title: 'Predictable Monthly Cost',
      description: 'A fixed monthly investment helps agencies maintain stable profit margins.'
    }
  ];

  const faqs = [
    {
      question: 'Is this only for agencies?',
      answer: `Yes. This white label digital marketing service is designed exclusively for digital marketing agencies in ${config.country}.`
    },
    {
      question: 'Will my clients know about your team?',
      answer: 'No. Everything is delivered under your agency brand.'
    },
    {
      question: 'Can we scale plans as we grow?',
      answer: 'Yes. Agencies can upgrade execution capacity anytime.'
    },
    {
      question: config.timezoneQuestion,
      answer: config.timezoneAnswer
    }
  ];

  const portfolioHighlights = [
    { brand: 'Afzaan', logo: '/images/clients/afzaan.png', result: '3.4x ROAS in 120 days' },
    { brand: 'Agile Ortho', logo: '/images/clients/agile-ortho.png', result: '42% increase in qualified leads' },
    { brand: 'My Fragrance', logo: '/images/clients/myfragrance.png', result: '2.1x conversion growth' },
    { brand: 'Top Crew', logo: '/images/clients/topcrew.png', result: '58% lower CPA' }
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const svgBg = "bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')]";

  const contactForm = (idSuffix: string, gridClass: string = '') => (
    submitStatus === 'success' ? (
      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
        <FiCheck className="text-5xl text-green-600 mx-auto mb-4" />
        <h4 className="text-xl font-semibold text-green-800 mb-2">Thank You!</h4>
        <p className="text-green-700">We'll be in touch with you shortly to discuss your white label partnership.</p>
      </div>
    ) : (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className={gridClass}>
          <div>
            <label htmlFor={`name-${idSuffix}`} className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" id={`name-${idSuffix}`} name="name" value={formData.name} onChange={handleChange} required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
              placeholder="Your Name" />
          </div>
          <div>
            <label htmlFor={`phone-${idSuffix}`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input type="tel" id={`phone-${idSuffix}`} name="phone" value={formData.phone} onChange={handleChange} required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
              placeholder={config.phonePlaceholder} />
          </div>
        </div>
        <div>
          <label htmlFor={`email-${idSuffix}`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
          <input type="email" id={`email-${idSuffix}`} name="email" value={formData.email} onChange={handleChange} required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
            placeholder="your@email.com" />
        </div>
        <div>
          <label htmlFor={`websiteUrl-${idSuffix}`} className="block text-sm font-medium text-gray-700 mb-1">Website URL *</label>
          <input type="url" id={`websiteUrl-${idSuffix}`} name="websiteUrl" value={formData.websiteUrl} onChange={handleChange} required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
            placeholder="https://youragency.com" />
        </div>
        <div>
          <label htmlFor={`cityState-${idSuffix}`} className="block text-sm font-medium text-gray-700 mb-1">City / Region *</label>
          <input type="text" id={`cityState-${idSuffix}`} name="cityState" value={formData.cityState} onChange={handleChange} required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
            placeholder={config.cityPlaceholder} />
        </div>
        <div>
          <label htmlFor={`employeeSize-${idSuffix}`} className="block text-sm font-medium text-gray-700 mb-1">Agency Employee Size *</label>
          <select id={`employeeSize-${idSuffix}`} name="employeeSize" value={formData.employeeSize} onChange={handleChange} required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all">
            <option value="">Select Size</option>
            <option value="1-10">1-10 Employees</option>
            <option value="11-20">11-20 Employees</option>
            <option value="21-50">21-50 Employees</option>
            <option value="51-100">51-100 Employees</option>
          </select>
        </div>
        {connectingMessage && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
            <p className="text-sm text-blue-700">{connectingMessage}</p>
          </div>
        )}
        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}
        <button type="submit" disabled={isSubmitting}
          className="w-full px-6 py-4 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
          {isSubmitting ? (
            <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {connectingMessage ? 'Connecting...' : 'Submitting...'}</>
          ) : (
            <><FiSend />Submit</>
          )}
        </button>
        <p className="text-xs text-gray-500 text-center">Only for Digital Marketing Agencies</p>
      </form>
    )
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900 pt-0">
        <div className={`absolute inset-0 ${svgBg} opacity-10`}></div>
        <div className="absolute top-0 left-0 right-0 z-20 border-b border-white/20 bg-black/20 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <Logo size="medium" className="bg-white rounded-md px-2 py-1" />
            <div className="flex items-center justify-end text-right text-white/95 text-xs sm:text-sm">
              <a href="mailto:contact@miranimarketing.com" className="flex items-center gap-2 hover:text-white transition-colors">
                <FiMail className="text-blue-200" />
                <span>contact@miranimarketing.com</span>
              </a>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10 w-full py-12 pt-36 sm:pt-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Scale Your Digital Marketing Agency Without Hiring More Staff
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed">
                A dedicated execution team delivering SEO, AI-SEO, Google Ads, Meta Ads, Creative Production, and Landing Page support under your agency brand.
              </p>
              <p className="text-base md:text-lg text-white/80 mb-8 leading-relaxed">
                Mirani Marketing works as your trusted white label agency partner, helping {config.country} digital marketing companies grow faster without increasing internal payroll.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  '100% White Label Delivery',
                  `Built for ${config.country} Digital Marketing Agencies`,
                  'Dedicated Execution Teams',
                  '200+ Weekly Execution Hours Available'
                ].map((highlight, index) => (
                  <div key={index} className="flex items-center gap-2 text-white">
                    <FiCheckCircle className="text-green-400 flex-shrink-0" />
                    <span className="text-sm md:text-base">{highlight}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="group px-8 py-4 bg-white text-blue-700 rounded-lg font-semibold hover:bg-blue-50 transition-all duration-300 flex items-center gap-2 shadow-xl">
                Get White Label Pricing
                <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Start Your White-Label Agency Partnership</h3>
              {contactForm('top')}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust / Authority Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              A Reliable White Label Digital Marketing Service Built for Agencies
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Mirani Marketing provides a powerful white label solution for digital marketing agencies in {config.country} that want to expand service delivery without hiring additional specialists.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mt-4">
              Our team works quietly behind the scenes, allowing your agency to maintain full client ownership while we execute campaigns.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="text-5xl font-bold text-blue-700 mb-2">{stat.number}</div>
                <div className="text-gray-700 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => document.getElementById('contact-form-bottom')?.scrollIntoView({ behavior: 'smooth' })}
              className="group px-8 py-4 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-all duration-300 inline-flex items-center gap-2 shadow-lg">
              Request Agency Partnership
              <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Agency Pain Point Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">Why Most Digital Marketing Agencies Struggle to Scale</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              Running a digital marketing agency in {config.country} is not just about acquiring clients. The real challenge is managing execution.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-xl max-w-4xl mx-auto mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Most agencies experience these problems:</h3>
            <div className="space-y-4">
              {painPoints.map((point, index) => (
                <motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.1 }} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  </div>
                  <p className="text-gray-700 text-lg">{point}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <p className="text-xl text-gray-700 text-center max-w-3xl mx-auto mb-4">As your client base grows, operational complexity grows even faster.</p>
          <p className="text-2xl font-bold text-primary-600 text-center">This is where a professional white label digital marketing service becomes essential.</p>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-20 bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Your Dedicated White Label Execution Team</h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">Mirani Marketing becomes a true white label partner for your agency.</p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="flex justify-center">
              {!pricingSectionImageMissing && (
                <img src={pricingSectionImageSources[pricingSectionImageIndex]} alt="White Label Partnership Growth"
                  className="w-full max-w-md rounded-2xl shadow-2xl"
                  onError={() => {
                    if (pricingSectionImageIndex < pricingSectionImageSources.length - 1) {
                      setPricingSectionImageIndex(pricingSectionImageIndex + 1);
                    } else {
                      setPricingSectionImageMissing(true);
                    }
                  }} />
              )}
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-1 gap-6">
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6">Your agency continues to:</h3>
                <div className="space-y-3">
                  {['Manage client communication', 'Handle strategy', 'Control pricing'].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <FiCheckCircle className="text-green-400 flex-shrink-0 text-xl" />
                      <span className="text-lg">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6">We execute:</h3>
                <p className="text-lg mb-4">While our expert team executes campaigns under your brand name.</p>
                <div className="bg-white/10 border-l-4 border-blue-300 rounded-r-xl px-6 py-5">
                  <p className="text-lg font-semibold text-blue-100 leading-relaxed">Your clients never know we exist.</p>
                  <p className="text-sm text-blue-200 mt-1 opacity-80">100% white-label — your name, your logo, your reputation.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Portfolio Highlights: Brands & Results</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">A snapshot of outcomes our team has supported for agency partners and their clients.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {portfolioHighlights.map((item, index) => (
              <motion.div key={item.brand} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="h-14 flex items-center justify-center mb-5">
                  <img src={item.logo} alt={`${item.brand} logo`} className="h-12 w-auto object-contain" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.brand}</h3>
                <p className="text-primary-600 font-semibold">{item.result}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services We Execute */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Complete White Label Digital Marketing Execution</h2>
            <p className="text-xl text-gray-600">Our team supports agencies with full-stack marketing execution.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <service.icon className="text-5xl text-blue-700 mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <FiCheckCircle className="text-primary-600 flex-shrink-0 mt-1" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button onClick={() => document.getElementById('contact-form-bottom')?.scrollIntoView({ behavior: 'smooth' })}
              className="group px-8 py-4 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-all duration-300 inline-flex items-center gap-2 shadow-lg">
              Contact Us Today
              <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section id="pricing-section" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Choose the Right Execution Team for Your Agency</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {pricingPlans.map((plan, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`rounded-2xl p-8 shadow-xl transition-all duration-300 hover:shadow-2xl ${plan.highlighted ? 'bg-gradient-to-br from-blue-700 to-blue-800 text-white ring-4 ring-blue-300 transform scale-105' : 'bg-white'}`}>
                {plan.highlighted && (
                  <div className="bg-primary-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
                )}
                <h3 className={`text-2xl font-bold mb-2 ${plan.highlighted ? 'text-white' : 'text-gray-800'}`}>{plan.name}</h3>
                <p className={`text-sm mb-4 ${plan.highlighted ? 'text-blue-100' : 'text-gray-600'}`}>{plan.subtitle}</p>
                <div className="mb-4">
                  <span className={`text-4xl font-bold ${plan.highlighted ? 'text-white' : 'text-blue-700'}`}>{plan.price}</span>
                  <span className={`text-sm ${plan.highlighted ? 'text-blue-100' : 'text-gray-600'}`}> / month</span>
                </div>
                <div className={`text-sm font-medium mb-6 ${plan.highlighted ? 'text-blue-100' : 'text-gray-700'}`}>{plan.capacity}</div>
                <div className="mb-6">
                  <h4 className={`font-bold mb-3 ${plan.highlighted ? 'text-white' : 'text-gray-800'}`}>Dedicated Team:</h4>
                  <ul className="space-y-2">
                    {plan.team.map((member, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <FiCheckCircle className={`flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-green-300' : 'text-green-600'}`} />
                        <span className={plan.highlighted ? 'text-blue-50' : 'text-gray-700'}>{member}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-6">
                  <h4 className={`font-bold mb-3 ${plan.highlighted ? 'text-white' : 'text-gray-800'}`}>Services Included:</h4>
                  <ul className="space-y-2">
                    {plan.services.map((service, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <FiCheckCircle className={`flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-green-300' : 'text-green-600'}`} />
                        <span className={plan.highlighted ? 'text-blue-50' : 'text-gray-700'}>{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-6">
                  <h4 className={`font-bold mb-3 ${plan.highlighted ? 'text-white' : 'text-gray-800'}`}>Reporting:</h4>
                  <ul className="space-y-2">
                    {plan.reporting.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <FiCheckCircle className={`flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-green-300' : 'text-green-600'}`} />
                        <span className={plan.highlighted ? 'text-blue-50' : 'text-gray-700'}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`text-sm font-medium mb-6 ${plan.highlighted ? 'text-blue-100' : 'text-gray-700'}`}>
                  <strong>Execution Capacity:</strong> {plan.executionHours}
                </div>
                <button onClick={() => document.getElementById('contact-form-bottom')?.scrollIntoView({ behavior: 'smooth' })}
                  className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${plan.highlighted ? 'bg-white text-blue-700 hover:bg-gray-100' : 'bg-blue-700 text-white hover:bg-blue-800'}`}>
                  Choose This Plan
                </button>
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="bg-primary-50 border-2 border-primary-200 rounded-xl p-6 text-center max-w-3xl mx-auto">
            <h4 className="text-xl font-bold text-gray-800 mb-2">How Projects Are Counted?</h4>
            <p className="text-gray-700">Each service type for a client is treated as an individual project. For example, SEO, Google Ads, and Meta Ads for the same client will be counted as three separate projects.</p>
          </motion.div>
        </div>
      </section>

      {/* Why Agencies Choose This */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why {config.country} Agencies Choose Our White Label Digital Marketing Service
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {whyChoose.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <item.icon className="text-5xl text-blue-700 mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-3">{item.title}</h3>
                <p className="text-gray-700">{item.description}</p>
              </motion.div>
            ))}
          </div>
          {/* Cost Comparison */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl p-8 md:p-12 max-w-4xl mx-auto text-white">
            <h3 className="text-3xl font-bold mb-6 text-center">Hiring Specialists in {config.country} Costs:</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {config.costComparison.map((item, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 flex justify-between items-center">
                  <span className="font-semibold text-lg">{item.role}</span>
                  <span className="text-primary-400 font-bold text-xl">{item.cost}</span>
                </div>
              ))}
            </div>
            <div className="bg-green-500/20 border-2 border-green-400 rounded-lg p-6 text-center">
              <p className="text-2xl font-bold mb-2">Our white label solution provides a complete team at a fraction of the cost.</p>
              <p className="text-green-300">Save thousands while scaling faster</p>
            </div>
          </motion.div>
          <div className="text-center mt-12">
            <button onClick={() => document.getElementById('contact-form-bottom')?.scrollIntoView({ behavior: 'smooth' })}
              className="group px-8 py-4 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-all duration-300 inline-flex items-center gap-2 shadow-lg text-lg">
              Become a Partner
              <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* How The Partnership Works */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">How The Partnership Works</h2>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: 'Agency Onboarding', description: 'We learn about your agency and align on processes' },
              { step: 2, title: 'Dedicated Team Setup', description: 'Your custom execution team is assembled and ready' },
              { step: 3, title: 'Project Execution', description: 'We execute campaigns under your brand name' },
              { step: 4, title: 'White Label Reporting', description: 'Branded reports delivered directly to you' }
            ].map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-full flex items-center justify-center font-bold text-3xl mx-auto mb-6 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Client Testimonials</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Real feedback from agencies that trust our white-label execution support.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {config.testimonials.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <p className="text-gray-700 text-lg leading-relaxed mb-6">"{item.quote}"</p>
                <p className="text-blue-700 font-semibold mb-1">{item.author}</p>
                <p className="text-sm text-gray-600">{item.result}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-gray-100 transition-colors">
                  <span className="font-semibold text-lg text-gray-800">{faq.question}</span>
                  <FiArrowRight className={`text-primary-600 text-xl transition-transform duration-300 ${openFaqIndex === index ? 'rotate-90' : ''}`} />
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section with Form */}
      <section id="contact-form-bottom" className={`py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 relative overflow-hidden`}>
        <div className={`absolute inset-0 ${svgBg} opacity-10`}></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center max-w-4xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Scale Your Digital Marketing Agency Faster?</h2>
            <p className="text-xl text-white/90">Partner with Mirani Marketing and get a dedicated white label execution team.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Get In Touch</h3>
            {contactForm('bottom', 'grid md:grid-cols-2 gap-4')}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <Logo size="medium" className="mx-auto mb-4 bg-white rounded-md px-2 py-1 inline-block" />
          <p className="text-gray-400 text-sm">© {new Date().getFullYear()} Mirani Marketing. All rights reserved.</p>
          <p className="text-gray-500 text-xs mt-2">
            <a href="mailto:contact@miranimarketing.com" className="hover:text-white transition-colors">contact@miranimarketing.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};
